import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { CreNgConfig, Jwt, JwtInvalidateResponse, RolesService } from 'cre-ng';
import { Observable, Subject, Subscription } from 'rxjs';

import dayjs from 'dayjs';
import { User } from './core.model';
import { WindowService } from './window.service';
import { IdleService } from './services/idle.service';

/** @ignore */
const dayjs_ = dayjs;

@Injectable()
export class AuthService {
  isViewable: boolean;
  login$: Subject<any>;
  timeout$: Subject<any>;
  timerStart$: Subject<any>;
  logout$: Subject<any>;
  dialogConfirmed$: Observable<boolean>;

  private appUrl: string;
  private casServiceUrl: string;
  private encryptedJwt: string;
  private currentUser: User;
  private VIEW_ROLE = 'ROLE_VIEW_DRIVER_4DX';
  private viewedUser: User;
  private sessionIsEnabled: boolean;
  private sessionSeconds: number;
  private sessionAppName: string;
  private idleCheckIsEnabled: boolean;
  private idle: IdleService;
  private timeoutSub: Subscription;
  private timerStartSub: Subscription;
  private isToolboxApp: boolean;

  private readonly dialogConfirmedSub: Subject<boolean>;

  constructor(
    private creNgConfig: CreNgConfig,
    private httpClient: HttpClient,
    private injector: Injector,
    private rolesService: RolesService,
    @Inject(WindowService) private windowService: Window
  ) {
    this.idleCheckIsEnabled = creNgConfig.idleCheck ? true : false;
    this.sessionIsEnabled = creNgConfig.session ? true : false;
    this.sessionSeconds = creNgConfig.sessionSeconds;
    this.sessionAppName = creNgConfig.sessionAppName;
    this.login$ = new Subject();
    this.logout$ = new Subject();
    this.timeout$ = new Subject();
    this.timerStart$ = new Subject();
    const url = new URL(this.windowService.location.href);

    if (this.idleCheckIsEnabled) {
      this.idle = this.injector.get(IdleService);
    }

    if (url.pathname !== '/' && url.pathname.indexOf('search') === -1) {
      const splittedPath = url.pathname.split('/');
      const lastPath = splittedPath[splittedPath.length - 1];
      if (lastPath !== '') {
        this.viewedUser = new User(lastPath);
      }
    }

    this.appUrl = url.origin + url.pathname;
    this.casServiceUrl = `${this.creNgConfig.serviceApiDomain}/security/api/v1`;

    this.dialogConfirmedSub = new Subject<boolean>();
    this.dialogConfirmed$ = this.dialogConfirmedSub.asObservable();
  }

  getCurrentUser() {
    if (this.isViewable && this.viewedUser != null) {
      return this.viewedUser;
    }
    return this.currentUser;
  }

  isAuthenticated() {
    return this.currentUser instanceof User;
  }

  isViewed() {
    return this.isViewable && this.viewedUser != null;
  }

  async isTicketValid(ticket: string): Promise<boolean> {
    this.isToolboxApp = false;
    let httpParams = new HttpParams();

    httpParams = httpParams.append('jwt', ticket);

    return this.httpClient
      .get<Jwt>(`${this.casServiceUrl}/jwt/validate`, { params: httpParams })
      .toPromise()
      .then(async (jwt: Jwt) => {
        if (!jwt.valid || jwt.expired) {
          this.logout();
          return false;
        }

        if (jwt.valid && !jwt.expired) {
          await this.initSession(ticket, jwt);

          if (this.isSessionActive()) {
            return true;
          } else {
            this.logout();
            return false;
          }
        }
      });
  }

  // Obsolete
  isTicketValidToolbox(ticket: string): Promise<boolean> {
    this.isToolboxApp = true;
    let httpParams = new HttpParams();

    httpParams = httpParams.append('service', this.appUrl);
    httpParams = httpParams.append('ticket', ticket);

    return this.httpClient
      .get(`${this.creNgConfig.serviceApiDomain}/sso/serviceValidate`, {
        params: httpParams,
        responseType: 'text',
      })
      .toPromise()
      .then(async (response) => {
        const domParser = new DOMParser();
        const xmlResponse = domParser.parseFromString(response, 'text/xml');
        if (xmlResponse.getElementsByTagName('cas:authenticationSuccess').length > 0) {
          this.setCurrentUser(
            new User(xmlResponse.getElementsByTagName('cas:user')[0].textContent)
          );

          const roleResponse = await this.rolesService.getForUser(this.currentUser.username);
          this.isViewable = roleResponse.body.includes(this.VIEW_ROLE);
          this.login$.next();
          return true;
        } else {
          return false;
        }
      });
  }

  async jwtIsMissing(encryptedJwt: string) {
    const localJwt = this.getLocalJwt();
    let result = false;
    if (!encryptedJwt) {
      if (localJwt) {
        this.encryptedJwt = localJwt;
        await this.isTicketValid(localJwt).then((res) => {
          return (result = res);
        });
        return result;
      } else {
        this.redirectToLogin();
        return true;
      }
    } else {
      this.encryptedJwt = encryptedJwt;
      return false;
    }
  }

  // Obsolete
  loginToolbox(token: string = '') {
    this.isToolboxApp = true;
    let redirectTo = `${this.casServiceUrl}/login?service=${encodeURI(this.appUrl)}`;

    if (token !== '') {
      redirectTo += `&token=${token}`;
    }

    this.windowService.location.href = redirectTo;
  }

  login(token: string = '') {
    let redirectTo = `${this.serviceUrl}/login?service=${encodeURI(this.appUrl + '?jwt=true')}`;
    this.isToolboxApp = false;
    if (token !== '') {
      redirectTo += `&token=${token}`;
    }

    this.windowService.location.href = redirectTo;
  }

  logout() {
    if (this.isToolboxApp === true) {
      const redirectTo = `${this.creNgConfig.serviceApiDomain}/sso/logout?service=${encodeURI(
        this.appUrl
      )}`;
      this.login$.unsubscribe();
      this.setCurrentUser(undefined);
      this.isViewable = false;
      this.windowService.location.href = redirectTo;
    } else {
      if (this.idleCheckIsEnabled) {
        this.idle.stopIdleTimer();
        this.unsub();
      }

      if (this.sessionIsEnabled) {
        void this.invalidateSession().then(() => {
          localStorage.removeItem(`cre_${this.sessionAppName}_auth`);
          localStorage.removeItem(`cre_${this.sessionAppName}_expires_at`);
        });
        this.login$.unsubscribe();
        this.setCurrentUser(undefined);
        this.isViewable = false;

        this.redirectToLogout();
      }
    }

    // this.logout$.next();
  }

  async invalidateSession(): Promise<boolean> {
    const jwt = this.getLocalJwt();
    let params = new HttpParams();
    params = params.append('jwt', jwt);

    return this.httpClient
      .post<JwtInvalidateResponse>(`${this.casServiceUrl}/jwt/invalidate`, params)
      .toPromise()
      .then((response: JwtInvalidateResponse) => {
        return response.success;
      });
  }

  refreshSession() {
    this.dialogConfirmedSub.next(true);

    const expiresAt = dayjs_().add(this.sessionSeconds, 'second');

    localStorage.setItem(
      `cre_${this.sessionAppName}_expires_at`,
      JSON.stringify(expiresAt.valueOf())
    );

    if (this.idleCheckIsEnabled) {
      this.idle.resetTimer();
    }
  }

  protected getEndPoint(): string {
    return '/sso/';
  }

  protected get serviceUrl(): string {
    const result = this.creNgConfig.serviceApiDomain + this.getEndPoint();

    return result;
  }

  private redirectToLogout() {
    const redirectTo = `${this.serviceUrl}/logout?service=${encodeURIComponent(
      this.appUrl + '?jwt=true'
    )}`;

    this.redirect(redirectTo);
  }

  private getLocalJwt() {
    return localStorage.getItem(`cre_${this.sessionAppName}_auth`)
      ? localStorage.getItem(`cre_${this.sessionAppName}_auth`)
      : null;
  }

  private redirect(url: string) {
    window.location.href = url;
  }

  private redirectToLogin() {
    if (this.isToolboxApp === true) {
      this.loginToolbox();
    } else {
      const redirectTo = `${this.serviceUrl}/login?service=${encodeURIComponent(
        this.appUrl + '?jwt=true'
      )}`;

      this.redirect(redirectTo);
    }
  }

  private unsub() {
    if (!this.timeoutSub) {
      return;
    }

    if (this.idleCheckIsEnabled) {
      this.timeoutSub.unsubscribe();
      this.timerStartSub.unsubscribe();
    }
  }

  private setCurrentUser(user: User) {
    this.currentUser = user;
  }

  private getSessionExpiration() {
    const expiration = atob(localStorage.getItem(`cre_${this.sessionAppName}_expires_at`));
    const expiresAt = JSON.parse(expiration);
    return dayjs_(expiresAt);
  }

  private sub() {
    this.login$.next();

    if (this.idleCheckIsEnabled) {
      this.timeoutSub = this.idle.timeout$.subscribe(() => {
        this.timeout$.next();
      });

      this.timerStartSub = this.idle.timerStart$.subscribe((count) => {
        this.timerStart$.next(count);
      });

      this.timeout$.subscribe((_) => {
        this.logout();
      });
    }
  }

  private isSessionActive() {
    this.sub();

    let isSessionActive: boolean;

    if (this.sessionIsEnabled) {
      isSessionActive = dayjs_().isBefore(this.getSessionExpiration());
    }
    if (this.idleCheckIsEnabled) {
      this.idle.startIdleTimer();
    }

    return this.sessionIsEnabled ? isSessionActive : true;
  }

  private async initSession(encryptedJwt: string, jwt: Jwt) {
    const username = jwt.data.sub;

    const roleResponse = await this.rolesService.getForUser(username);
    this.isViewable = roleResponse.body.includes(this.VIEW_ROLE);

    this.setCurrentUser(new User(username));

    if (this.sessionIsEnabled) {
      const expiresAt = dayjs_().add(this.sessionSeconds, 'second');

      localStorage.setItem(
        `cre_${this.sessionAppName}_expires_at`,
        btoa(JSON.stringify(expiresAt.valueOf()))
      );
      localStorage.setItem(`cre_${this.sessionAppName}_auth`, encryptedJwt);
    }

    this.login$.next();
  }
}
